import React from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, SubHeading, Text} from '@forecast-it/design-system';
import {FeatureFlagAssignmentRow} from './featureFlagDetailData';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_CARD_HOVER_BACKGROUND,
	FEATURE_FLAG_NEUTRAL_FILL,
	featureFlagCardSurface,
} from './featureFlagSurface';

// Shared by the column header and the rows so the two can never drift apart, the same way the flag
// list shares its grid. Narrower than the list's, because this table has four columns rather than six.
const ASSIGNMENT_COLUMNS = 'minmax(180px, 1.1fr) minmax(240px, 1.5fr) 130px 44px';
const ASSIGNMENT_GAP = '16px';
const ASSIGNMENT_MIN_WIDTH = '860px';

const TableCard = styled.div`
	${featureFlagCardSurface}
	margin-top: 20px;
	overflow-x: auto;
`;

const TableHead = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 20px;
	padding: 14px 18px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const TableTitle = styled.div`
	display: flex;
	align-items: baseline;
	gap: 10px;
`;

// Holds the column header and the rows, and nothing else, so the whole of it — and only it — can
// announce as a table. It is what carries the width the columns need, with the header and rows
// stretching to fill it, so the card around it scrolls them together.
const TableGrid = styled.div`
	min-width: ${ASSIGNMENT_MIN_WIDTH};
`;

const ColumnHeader = styled.div`
	display: grid;
	grid-template-columns: ${ASSIGNMENT_COLUMNS};
	gap: ${ASSIGNMENT_GAP};
	padding: 9px 18px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

/**
 * A column heading, set small and wide-tracked so it reads as a label rather than as a line of copy.
 *
 * The tracking has to be declared on the child rather than here: the typography components emit their
 * own letter-spacing onto the element they render, and a declaration on that element beats one
 * inherited from this wrapper.
 */
const MicroLabel = styled.span`
	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const Row = styled.div`
	display: grid;
	grid-template-columns: ${ASSIGNMENT_COLUMNS};
	gap: ${ASSIGNMENT_GAP};
	align-items: center;
	min-height: 44px;
	padding: 8px 18px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};

	&:hover {
		background: ${FEATURE_FLAG_CARD_HOVER_BACKGROUND};
	}
`;

// The grid rows announce as table rows, so the label and date each need a cell of their own to be
// announced in — the typography component alone is the content, not the cell.
const Cell = styled.span`
	display: flex;
	align-items: center;
	min-width: 0;
`;

const UsersCell = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const ActionCell = styled.span`
	display: flex;
	justify-content: flex-end;
`;

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 56px 20px;
`;

/**
 * Placeholder for the rollout modal's entry points. The controls are rendered enabled and in place,
 * so the page is complete at rest, and wiring the modal up later replaces this with a real handler
 * rather than adding UI that was not there before.
 */
const doNothingUntilRolloutModalExists = (): void => undefined;

export interface FeatureFlagAssignmentTableProps {
	rows: readonly FeatureFlagAssignmentRow[];
}

export function FeatureFlagAssignmentTable({rows}: FeatureFlagAssignmentTableProps): JSX.Element {
	// Counts the companies listed below, so it includes companies that hold the flag only through
	// named users. That makes it legitimately larger than the Companies stat card, which counts
	// all-users grants.
	const countLabel =
		rows.length === 0 ? 'No companies assigned' : rows.length === 1 ? '1 company' : `${rows.length} companies`;

	return (
		<TableCard>
			<TableHead>
				<TableTitle>
					<SubHeading>Currently assigned</SubHeading>
					<Detail color="medium">{countLabel}</Detail>
				</TableTitle>
			</TableHead>
			{/* A CSS grid rather than a real table, because the layout is card-and-grid throughout this
			    feature — so the roles have to say out loud what the markup would otherwise have implied. */}
			<TableGrid role="table" aria-label="Companies with this flag">
				<ColumnHeader role="row">
					<MicroLabel role="columnheader">
						<Detail size="2">COMPANY</Detail>
					</MicroLabel>
					<MicroLabel role="columnheader">
						<Detail size="2">USERS</Detail>
					</MicroLabel>
					<MicroLabel role="columnheader">
						<Detail size="2">ADDED</Detail>
					</MicroLabel>
					{/* The edit column is headed by nothing on the page, so its name is given here rather
					    than leaving a screen reader to announce a nameless fourth column. */}
					<span role="columnheader" aria-label="Actions" />
				</ColumnHeader>
				{rows.map(row => (
					<Row key={row.companyId} data-cy="feature-flag-assignment-row" role="row">
						<Cell role="cell">
							<Text>{row.companyName}</Text>
						</Cell>
						<UsersCell role="cell">
							{row.allUsers ? (
								<Detail>All users</Detail>
							) : (
								<>
									<Text>{row.userCountLabel}</Text>
									{row.users.map((user, index) => (
										<Detail color="medium" key={`${row.companyId}-${index}`}>
											{user}
										</Detail>
									))}
								</>
							)}
						</UsersCell>
						<Cell role="cell">
							<Detail color="medium">{row.added ?? '—'}</Detail>
						</Cell>
						<ActionCell role="cell">
							<Button
								aria-label={`Edit access for ${row.companyName}`}
								emphasis={EMPHASIS.LOW}
								icon="edit"
								iconOnly
								onPress={doNothingUntilRolloutModalExists}
							/>
						</ActionCell>
					</Row>
				))}
			</TableGrid>
			{rows.length === 0 ? (
				<EmptyState>
					<SubHeading>No companies have this flag yet</SubHeading>
					<Detail>{'Use “Roll out to more companies” to start a percentage rollout or add one company.'}</Detail>
				</EmptyState>
			) : null}
		</TableCard>
	);
}
